import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { format, startOfMonth, startOfYear, subMonths } from "date-fns";
import type { DateRange } from "react-day-picker";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import SectionCard from "@/components/common/SectionCard";
import EmptyState from "@/components/common/EmptyState";
import ExportButton from "@/components/common/ExportButton";
import DateRangeFilter from "@/components/common/DateRangeFilter";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useGetOrganizationReport } from "@/hooks/useReports";
import { buildBillingPath } from "@/routes/url";
import { downloadCsv, formatCompactMoney } from "@/utils/common.utils";
import { formatMoney } from "@/utils/deals.utils";
import type { OrganizationReportCounts } from "@/types/reports.types";

const ALL_AGENTS = "all";
const RANGES = [
  { value: "month", label: "This month" },
  { value: "3", label: "Last 3 months" },
  { value: "12", label: "Last 12 months" },
  { value: "ytd", label: "Year to date" },
  { value: "custom", label: "Custom range" },
];

const day = (d: Date) => format(d, "yyyy-MM-dd");
const monthLabel = (month: string) => format(new Date(`${month}-01T12:00:00`), "MMM yyyy");

function rangeParams(range: string, custom?: DateRange) {
  const now = new Date();
  if (range === "custom" && custom?.from) return { from: day(custom.from), to: day(custom.to ?? custom.from) };
  if (range === "month") return { from: day(startOfMonth(now)), to: day(now) };
  if (range === "ytd") return { from: day(startOfYear(now)), to: day(now) };
  // A cleared custom range falls back to the last 12 months instead of NaN.
  return { from: day(startOfMonth(subMonths(now, (Number(range) || 12) - 1))), to: day(now) };
}

function Tile({ label, value, hint }: { label: string; value: string | number; hint: string }) {
  return (
    <SectionCard>
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-dash-faint">{label}</p>
      <p className="mt-3 text-3xl font-bold tracking-tight text-dash-ink">{value}</p>
      <p className="mt-2 text-xs text-dash-faint">{hint}</p>
    </SectionCard>
  );
}

/**
 * Reports → Organization (Growth and up): listings and contracts right now,
 * closings and compensation by month in the chosen window, per agent and for
 * the whole org. Scope and money are decided on the server: agents get only
 * their own deals, coordinators get no money fields at all.
 */
export default function OrganizationReport() {
  const [range, setRange] = useState("12");
  const [custom, setCustom] = useState<DateRange | undefined>({ from: startOfYear(new Date()), to: new Date() });
  const [agent, setAgent] = useState(ALL_AGENTS);
  const params = useMemo(
    () => ({ ...rangeParams(range, custom), agent_id: agent === ALL_AGENTS ? undefined : agent }),
    [range, custom, agent],
  );
  const { data, isLoading } = useGetOrganizationReport(params);

  if (isLoading || !data) return null;

  if (data.locked) {
    return (
      <SectionCard className="mt-6" data-testid="org-report-locked">
        <div className="flex flex-col items-start gap-3">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-dash-faint"><Lock className="size-3.5" aria-hidden /> Growth plan and up</span>
          <p className="text-lg font-semibold text-dash-ink">Organization reporting</p>
          <p className="max-w-xl text-sm text-dash-muted">
            See active listings, deals under contract, monthly closings and compensation for every agent and the whole brokerage, with date ranges and CSV exports.
          </p>
          {data.org_slug && (
            <Button asChild className="mt-1 rounded-xl"><Link to={buildBillingPath(data.org_slug)}>Upgrade to Growth</Link></Button>
          )}
        </div>
      </SectionCard>
    );
  }

  const money = data.can_see_money;
  const period = `${data.period.from} to ${data.period.to}`;
  const monthMax = Math.max(1, ...data.months.map((m) => m.closings));
  const counts = (row: OrganizationReportCounts) => [
    row.active_listings, row.under_contract, row.closings, row.volume,
    ...(money ? [row.earned ?? 0, row.expected ?? 0] : []),
  ];
  const countHeaders = ["Active listings", "Under contract", "Closings", "Volume", ...(money ? ["Earned", "Expected"] : [])];

  const exportSummary = () => {
    if (!data.by_agent.length) {
      toast.info("There is no data to export");
      return;
    }
    downloadCsv({
      filename: "organization-report-summary.csv",
      rows: [
        ["Period", period],
        [],
        ["Agent", ...countHeaders],
        ...data.by_agent.map((row) => [row.agent_name, ...counts(row)]),
        ["Total", ...counts(data.totals)],
        [],
        ["Month", "Closings", "Volume", ...(money ? ["Earned"] : [])],
        ...data.months.map((m) => [m.month, m.closings, m.volume, ...(money ? [m.earned ?? 0] : [])]),
      ],
    });
  };

  const exportDeals = () => {
    if (!data.deals.length) {
      toast.info("There is no data to export");
      return;
    }
    downloadCsv({
      filename: "organization-report-deals.csv",
      rows: [
        ["Address", "Agent", "Stage", "Price", "Closed", "Closing date", ...(money ? ["Compensation %", "Compensation"] : [])],
        ...data.deals.map((d) => [
          d.address, d.agent_name, d.phase_label, d.price ?? "", d.closed_date, d.closing_date,
          ...(money ? [d.pct ?? "", d.amount ?? ""] : []),
        ]),
      ],
    });
  };

  return (
    <div className="mt-6" data-testid="org-report">
      <div className="flex flex-wrap items-center gap-2">
        {data.can_filter_agents && (
          <Select value={agent} onValueChange={setAgent}>
            <SelectTrigger className="h-11 w-48 rounded-xl" aria-label="Agent" data-testid="org-report-agent"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_AGENTS}>All agents</SelectItem>
              {data.agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name || "Unnamed agent"}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger className="h-11 w-44 rounded-xl" aria-label="Date range" data-testid="org-report-range"><SelectValue /></SelectTrigger>
          <SelectContent>
            {RANGES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {range === "custom" && (
          <DateRangeFilter value={custom} onChange={setCustom} testId="org-report-custom-range" />
        )}
        <div className="flex flex-wrap gap-2 sm:ml-auto">
          <ExportButton onExport={exportSummary} label="Export summary" className="h-11 !rounded-xl px-3" testId="org-report-export-summary" />
          <ExportButton onExport={exportDeals} label="Export deals" className="h-11 !rounded-xl px-3" testId="org-report-export-deals" />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 xl:grid-cols-3">
        <Tile label="Active listings" value={data.totals.active_listings} hint="On the market now" />
        <Tile label="Under contract" value={data.totals.under_contract} hint="Pending right now" />
        <Tile label="Closings" value={data.totals.closings} hint={`Closed ${period}`} />
        <Tile label="Volume closed" value={formatCompactMoney(data.totals.volume)} hint={`Closed ${period}`} />
        {money && (
          <>
            <Tile label="Earned compensation" value={formatCompactMoney(data.totals.earned)} hint="From deals closed in this range" />
            <Tile label="Expected compensation" value={formatCompactMoney(data.totals.expected)} hint="From deals under contract now" />
          </>
        )}
      </div>
      {!!data.missing_pct && (
        <p className="mt-3 text-xs text-dash-faint">
          {data.missing_pct} deal{data.missing_pct === 1 ? "" : "s"} under contract or closed {data.missing_pct === 1 ? "has" : "have"} no compensation % yet, so {data.missing_pct === 1 ? "it isn't" : "they aren't"} in the compensation totals.
        </p>
      )}

      <SectionCard
        className="mt-4"
        title="Monthly closings"
        description={`Closings: deals closed that month. Volume: the total sale price of those deals.${money ? " Earned: your compensation from them (sale price × compensation %)." : ""} A deal counts in the month of its closed date.`}
        data-testid="org-report-months"
      >
        <div className="space-y-3">
          {data.months.map((m) => (
            <div key={m.month} className="flex items-center gap-4">
              <span className="w-20 shrink-0 text-sm text-dash-muted">{monthLabel(m.month)}</span>
              <div className="h-3 flex-1 overflow-hidden rounded-full bg-dash-chip">
                <div className="bar-fill-w h-full rounded-full bg-primary" style={{ width: `${(m.closings / monthMax) * 100}%` }} />
              </div>
              <span className="w-56 shrink-0 text-right text-xs tabular-nums text-dash-faint">
                {m.closings} closing{m.closings === 1 ? "" : "s"} · {formatCompactMoney(m.volume)} volume{money ? ` · ${formatCompactMoney(m.earned)} earned` : ""}
              </span>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard className="mt-4" title="Results by agent" data-testid="org-report-agents">
        {data.by_agent.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                {countHeaders.map((h) => <TableHead key={h} className="text-right">{h}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...data.by_agent, { ...data.totals, agent_id: "total", agent_name: "Total" }].map((row) => (
                <TableRow key={row.agent_id ?? row.agent_name} className={row.agent_id === "total" ? "font-semibold" : undefined}>
                  <TableCell>{row.agent_name}</TableCell>
                  {counts(row).map((value, i) => (
                    <TableCell key={countHeaders[i]} className="text-right tabular-nums">
                      {i >= 3 ? formatMoney(value) || "$0" : value}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyState compact title="No deals in this range" description="Widen the date range or pick another agent." className="border-0" />
        )}
      </SectionCard>
    </div>
  );
}
